import { Box, Flex } from '@chakra-ui/react';
import { useState } from 'react';

import { FeatureFlag, type MetricValues } from '@revelio/core';
import {
  plotColors,
  SankeyChart,
  type SankeyData,
  type SankeyLink,
  type SankeyNode,
} from '@revelio/replots';

import { useUnleashFlag } from '../../../../../shared/unleash-flags/use-unleash-flag';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { TransitionBreakdownData } from '../../DataProvider/types';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import { useChart } from '../ChartProvider';
import { SharableChart } from '../chart-menu/plot-share/SharableChart';
import { buildThreeTierSankeyData } from './buildThreeTierSankeyData';
import { SankeyFooter } from './SankeyFooter';

const THREE_TIER_NODE_COUNT_OPTIONS = [5, 10, 15, 20];
const DEFAULT_NODE_COUNT_OPTIONS = [10, 15, 20, 25, 30];

const userNodeCountByChartId = new Map<string, number>();

interface BuildSankeyParams {
  data: TransitionBreakdownData[];
  useThreeTier: boolean;
  metric: MetricValues | undefined;
  effectiveNumOfNodes: number;
  color?: string;
}

const buildSankeyData = ({
  data,
  useThreeTier,
  metric,
  effectiveNumOfNodes,
  color,
}: BuildSankeyParams): SankeyData | null => {
  if (data.length === 0) return null;

  if (useThreeTier) {
    return buildThreeTierSankeyData({
      data,
      effectiveNumOfNodes,
      color,
    });
  }

  const isInflow = metric === 'transitions_inflow';
  const cycleSuffix = isInflow ? '__outflow' : '__inflow';

  const primaryColorAt = (idx: number): string => {
    if (idx === 0) return color ?? plotColors[0] ?? '#5CA9E9';
    return plotColors[idx % plotColors.length] ?? '#70CA97';
  };

  const primaryNodes: SankeyNode[] = [];
  const externalNodes: SankeyNode[] = [];
  const seenNodeIds = new Set<string>();
  const colorByPrimaryId = new Map<string, string>();
  const primaryIdSet = new Set<string>();

  data.forEach((entry, idx) => {
    const label = entry.dim ?? '';
    const id = entry.id ?? label;
    if (!id || seenNodeIds.has(id)) return;

    primaryNodes.push({ id, label });
    seenNodeIds.add(id);
    primaryIdSet.add(id);
    colorByPrimaryId.set(id, primaryColorAt(idx));
  });

  const externalTotalValue = new Map<string, number>();
  const links: SankeyLink[] = [];

  data.forEach((entry) => {
    const primaryId = entry.id ?? entry.dim ?? '';
    if (!primaryId) return;

    const primaryColor = colorByPrimaryId.get(primaryId) ?? primaryColorAt(0);
    const breakdownSlice = entry.breakdown?.slice(0, effectiveNumOfNodes) ?? [];

    breakdownSlice.forEach(({ dim, id, value, share }) => {
      const baseId = id ?? dim;
      const isCycle = primaryIdSet.has(baseId);
      const safeId = isCycle ? `${baseId}${cycleSuffix}` : baseId;
      const safeLabel = dim;

      if (!seenNodeIds.has(safeId)) {
        externalNodes.push({ id: safeId, label: safeLabel });
        seenNodeIds.add(safeId);
      }

      const linkValue = value ?? 0;
      externalTotalValue.set(
        safeId,
        (externalTotalValue.get(safeId) ?? 0) + linkValue
      );

      const sourceId = isInflow ? safeId : primaryId;
      const targetId = isInflow ? primaryId : safeId;

      links.push({
        source: sourceId,
        target: targetId,
        value: linkValue,
        share: share ?? 0,
        color: primaryColor,
        group: `${baseId}::${primaryId}`,
      });
    });
  });

  if (links.length === 0) return null;

  const orderedExternals = [...externalNodes].sort(
    (a, b) =>
      (externalTotalValue.get(b.id) ?? 0) - (externalTotalValue.get(a.id) ?? 0)
  );

  const nodes: SankeyNode[] = isInflow
    ? [...orderedExternals, ...primaryNodes]
    : [...primaryNodes, ...orderedExternals];

  return { nodes, links };
};

export const Sankey = ({ data }: { data: TransitionBreakdownData[] }) => {
  const metric = usePlotMetric();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const { item } = useChart();

  const color = item?.plot?.color;
  const showNodeLabels = item?.plot?.showDataLabels;

  const topNFilterValue = item?.plot?.plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;
  const isFixedCount =
    typeof topNFilterValue === 'number' && topNFilterValue > 0;

  const primaryEntries = data ?? [];
  const useThreeTier =
    isNewDashboardLayout && metric === 'transitions_inflows_and_outflows';

  const nodeCountOptions = useThreeTier
    ? THREE_TIER_NODE_COUNT_OPTIONS
    : DEFAULT_NODE_COUNT_OPTIONS;
  const chartId = item?.id;
  const autoNodeCount = useThreeTier && primaryEntries.length > 2 ? 5 : 10;
  const defaultNodeCount =
    isFixedCount && topNFilterValue != null ? topNFilterValue : autoNodeCount;
  const [userNodeCount, setUserNodeCount] = useState<number | null>(() =>
    chartId ? (userNodeCountByChartId.get(chartId) ?? null) : null
  );
  const numOfNodes = userNodeCount ?? defaultNodeCount;
  const showNodeCountSelect = useThreeTier || !isFixedCount;
  const effectiveNumOfNodes = showNodeCountSelect
    ? numOfNodes
    : (topNFilterValue ?? 10);

  const handleNodeCountChange = (value: number) => {
    setUserNodeCount(value);
    if (chartId) userNodeCountByChartId.set(chartId, value);
  };

  const sankeyData = buildSankeyData({
    data: primaryEntries,
    useThreeTier,
    metric,
    effectiveNumOfNodes,
    color,
  });

  if (!sankeyData) return null;

  return (
    <Flex
      position="relative"
      height="100%"
      flexDirection="column"
      paddingX="1rem"
    >
      <Box
        height={showNodeCountSelect ? 'calc(100% - 32px)' : '100%'}
        position="relative"
      >
        <SharableChart
          Component={SankeyChart}
          data={sankeyData}
          valueScale={useThreeTier ? 'sqrt' : 'linear'}
          color={color}
          showNodeLabels={showNodeLabels}
        />
      </Box>
      {showNodeCountSelect ? (
        <SankeyFooter
          numOfNodes={numOfNodes}
          onNumOfNodesChange={handleNodeCountChange}
          options={nodeCountOptions}
        />
      ) : null}
    </Flex>
  );
};
